/**
 * OrdenStatusFlow - Indicador visual de progreso del flujo de una orden.
 * Muestra los 4 pasos: Extracción → Pendiente → Con Resultado → Finalizada
 */

import { useTranslation } from "react-i18next";
import { Check, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { EstadoOrden } from "@/hooks/use-ordenes";

interface OrdenStatusFlowProps {
  estado: EstadoOrden;
}

const STEPS: { key: string; labelKey: string; estado: EstadoOrden }[] = [
  { key: "extraction", labelKey: "ordenes.statusFlow.extraction", estado: "EN_EXTRACCION" },
  { key: "pending", labelKey: "ordenes.statusFlow.pending", estado: "PENDIENTE" },
  { key: "withResults", labelKey: "ordenes.statusFlow.withResults", estado: "CON_RESULTADO" },
  { key: "finalized", labelKey: "ordenes.statusFlow.finalized", estado: "FINALIZADA" },
];

function getStepIndex(estado: EstadoOrden): number {
  const idx = STEPS.findIndex((s) => s.estado === estado);
  return idx >= 0 ? idx : -1;
}

export function OrdenStatusFlow({ estado }: OrdenStatusFlowProps) {
  const { t } = useTranslation();
  const isCancelled = estado === "BAJA";
  const currentIndex = getStepIndex(estado);

  return (
    <div
      className="flex items-center gap-1 w-full"
      role="list"
      aria-label={t("ordenes.statusFlow.extraction")}
    >
      {STEPS.map((step, i) => {
        const isCompleted = !isCancelled && currentIndex > i;
        const isCurrent = !isCancelled && currentIndex === i;

        return (
          <div key={step.key} className="flex items-center flex-1 min-w-0" role="listitem">
            <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
              {/* Círculo */}
              <div
                className={cn(
                  "h-6 w-6 rounded-full flex items-center justify-center text-[10px] font-medium shrink-0 transition-colors",
                  isCancelled && "bg-destructive/15 text-destructive border border-destructive/30",
                  !isCancelled && isCompleted && "bg-primary text-primary-foreground",
                  !isCancelled && isCurrent && "bg-primary/15 text-primary border-2 border-primary",
                  !isCancelled && !isCompleted && !isCurrent && "bg-muted text-muted-foreground"
                )}
              >
                {isCancelled ? (
                  <X className="h-3 w-3" />
                ) : isCompleted ? (
                  <Check className="h-3 w-3" />
                ) : (
                  i + 1
                )}
              </div>
              {/* Label */}
              <span
                className={cn(
                  "text-[10px] leading-tight text-center truncate w-full",
                  isCancelled && "text-destructive",
                  !isCancelled && (isCompleted || isCurrent) && "text-foreground font-medium",
                  !isCancelled && !isCompleted && !isCurrent && "text-muted-foreground"
                )}
              >
                {isCancelled && i === 0 ? t("ordenes.statusFlow.cancelled") : t(step.labelKey)}
              </span>
            </div>
            {/* Línea conectora */}
            {i < STEPS.length - 1 && (
              <div
                className={cn(
                  "h-0.5 w-4 shrink-0 -mt-4",
                  isCancelled && "bg-destructive/20",
                  !isCancelled && currentIndex > i && "bg-primary",
                  !isCancelled && currentIndex <= i && "bg-muted"
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
